import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserName from '../../common/user/UserName';
import { supportTicketHeader, supportTicketData } from '../../../constants/constants';

import NoUnits from '../../../images/no-units-found.svg';
import EyeIcon from '../../../images/eye.svg';

import styles from './SupportTicketListing.module.css';

import Select from 'react-select';
import Button from '../../common/button/Button';
import { RaiseSupportTicket } from '../modals/RaiseSupportTicket';
import CustModal from '../../common/custmodal/CustModal';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getSupportTickets } from '../../../redux/support-ticket/actions/support-ticket.action';
import Toast from '../../common/toast/Toast';
import Pagination from '../../common/pagination/pagination';
import Loader from '../../common/loader/Loader';
import { useDebounce } from '../../../utils/useDebounce';
import useWindowDimension from '../../../hooks/useWindowdimension';
import SearchInput from '../../common/search-input/SearchInput';
import moment from 'moment';
import { SupportTicket, statusFilterOptions } from '../interface/Support.interface';
import { getLocation } from '../../../redux/location/actions/location.action';
import { locationsData } from '../../locations/Locations';

function SupportTicketListing() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error'>('success');
    const supportTicketData = useSelector((state: IRootState) => state.supportTicketReducer.data);
    const totalCount = useSelector((state: IRootState) => state.supportTicketReducer.count);
    const loading = useSelector((state: IRootState) => state.supportTicketReducer.loading);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState<number>(1);
    const searchWithDebounce = useDebounce(searchTerm, 1000, setPageNumber);
    const pageLimit = 10;
    const windowWidth = useWindowDimension();
    const [selectedFilter, setSelectedFilter] = useState<{ value: string; label: string }>({
        value: '',
        label: 'Show All',
    });

    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const [locations, setLocations] = useState<locationsData[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<{ value: string; label: string }>({
        value: '',
        label: 'All Properties',
    });

    const colourStyles = {
        control: (base: AnyIfEmpty<object>) => ({
            ...base,
            height: 48,
            minHeight: 48,
            maxHeight: 48,
            outline: 'none',
        }),
        menu: (base: AnyIfEmpty<object>) => ({
            ...base,
            zIndex: 9,
        }),
        option: (styles: AnyIfEmpty<object>, { isDisabled, isFocused, isSelected }: AnyIfEmpty<object>) => {
            return {
                ...styles,
                backgroundColor: isSelected ? '#51ab86 !important' : isFocused ? '#DEEBFF' : null,
                color: isSelected ? '#fff !important' : '#000',
                cursor: isDisabled ? 'not-allowed' : 'default',
            };
        },
    };

    useEffect(() => {
        dispatch(getLocation(undefined, undefined, true));
    }, []);

    useEffect(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            setLocations([]);
        } else {
            if (invitedLocations?.length || allLocationsList?.length) {
                const data = invitedLocations?.map((list: locationsData) => ({
                    ...list,
                    isRestricted: true,
                }));
                setLocations([...(data ?? []), ...(allLocationsList ?? [])]);
            }
        }
    }, [allLocationsList?.length, invitedLocations?.length]);

    const locationOptions = useMemo(
        () => [
            { value: '', label: 'All Properties' },
            ...locations.map((loc: locationsData) => ({
                value: loc.id ?? '',
                label: loc.name ?? '',
            })),
        ],
        [locations]
    );

    const fetchTickets = () => {
        dispatch(
            getSupportTickets({
                searchText: searchTerm,
                page: pageNumber,
                limit: pageLimit,
                ticketStatus:
                    selectedFilter.value
                        ? [selectedFilter.value]
                        : undefined,
                location:
                    selectedLocation.value
                        ? [selectedLocation.value]
                        : undefined,
            })
        );
    };

    useEffect(() => {
        fetchTickets();
    }, [searchWithDebounce, pageNumber, selectedFilter.value, selectedLocation.value]);

    const [showRaiseTicketModal, setShowRaiseTicketModal] = useState(false);

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";

        return moment(dateString).format("MM-DD-YYYY, h:mm A");
    };

    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const handleFilterChange = (selectedOption: { value: string; label: string } | null) => {
        const option = selectedOption ?? { value: '', label: 'Show All' };
        setSelectedFilter(option);
        setPageNumber(1);
    }

    return (
        <React.Fragment>
            <div className={`${styles.supportPadding} outletConainer`}>
                {loading && <Loader />}
                <div className={styles.supportHead}>
                    <div className={styles.userGreet}>
                        <p>
                            Welcome <UserName />
                        </p>
                        <h2>Support Tickets</h2>
                    </div>
                    <Button
                        variant='green'
                        type='button'
                        className={styles.raiseTicketBtn}
                        title='Raise Support Ticket'
                        onClick={() => setShowRaiseTicketModal(true)}
                    >
                        Raise Support Ticket
                    </Button>

                </div>
                <div className={styles.searchFilterContainer}>
                    <SearchInput
                        value={searchTerm}
                        placeholder={"Search by Ticket ID, Subject, Location, Name and Email"}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: windowWidth > 1280 ? 920 : 240 }}
                    />
                    <div className={styles.btnContainer}>
                        <div className={styles.filterDropDown}>
                            <Select
                                onChange={(selectedOption) => {
                                    const option = selectedOption ?? { value: '', label: 'All Properties' };
                                    setSelectedLocation(option);
                                    setPageNumber(1);
                                }}
                                isSearchable={true}
                                classNamePrefix="custom-react-select"
                                value={
                                    selectedLocation.value !== ''
                                        ? { value: selectedLocation.value, label: selectedLocation.label }
                                        : { value: '', label: 'All Properties' }
                                }
                                options={locationOptions}
                                placeholder="Select Properties"
                                styles={colourStyles}
                            />
                        </div>
                    </div>
                    <div className={styles.btnContainer}>
                        <div className={styles.filterDropDown}>
                            <Select
                                onChange={(selectedOption) => {
                                    handleFilterChange(selectedOption);
                                }}
                                isSearchable={false}
                                classNamePrefix="custom-react-select"
                                value={
                                    selectedFilter.value !== ''
                                        ? { value: selectedFilter.value, label: selectedFilter.label }
                                        : { value: '', label: 'Show All' }
                                }
                                options={statusFilterOptions}
                                placeholder="Filter by Status"
                                styles={colourStyles}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.supportListing}>
                    <table className={`${styles.supportTable} SupportTicketListing`}>
                        <thead>
                            <tr>
                                {supportTicketHeader.map((item, index) => {
                                    return <th key={index}>{item.title}</th>;
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {supportTicketData.length > 0
                                ? supportTicketData.map((item: SupportTicket, index: number) => {
                                    return (
                                        <tr key={index}>
                                            <td className={styles.ticketId}>{item.id}</td>
                                            <td className={styles.subject}>{item.subject}</td>
                                            <td className={styles.location}>{item.location?.name || '-'}</td>
                                            <td className={styles.name}>{item.adminUser.fullName}</td>
                                            <td className={styles.email}>{item.adminUser.email}</td>
                                            <td>
                                                <span
                                                    className={`${styles.statusBadge} ${item.ticketStatus === "open"
                                                        ? styles.statusOpen
                                                        : item.ticketStatus === "closed"
                                                            ? styles.statusResolved
                                                            : item.ticketStatus === "re_open"
                                                                ? styles.statusInProgress
                                                                : ""
                                                        }`}
                                                >
                                                    {item.ticketStatus.replace("_", " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                                </span>
                                            </td>
                                            <td className={styles.createdAt}>{formatDate(item.createdAt)}</td>
                                            <td className={styles.updatedAt}>
                                                {item.updatedAt && item.updatedAt !== item.createdAt
                                                    ? formatDate(item.updatedAt)
                                                    : "-"}
                                            </td>
                                            <td className={styles.action}>
                                                <img
                                                    src={EyeIcon}
                                                    alt="view icon"
                                                    width={18}
                                                    height={18}
                                                    className={styles.editIcon}
                                                    onClick={() => navigate(`/support/ticket-details/${item.id}`)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                                : (!loading && supportTicketData.length === 0 && (
                                    <tr>
                                        <td colSpan={supportTicketHeader.length} style={{ textAlign: 'center' }}>
                                            <div className={styles.noUnits}>
                                                <img src={NoUnits} alt="no-units" />
                                                <p className={styles.noUnitsFoundText}>No Records Found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                {totalCount > 0 && (
                    <div className={styles.paginationWrapper}>
                        <Pagination
                            className="pagination-bar"
                            currentPage={pageNumber}
                            totalCount={totalCount}
                            pageSize={pageLimit}
                            onPageChange={(page: number) => setPageNumber(page)}
                        />
                    </div>
                )}
                {showToast && <Toast type={toastType} message={toastMessage} />}
            </div>
            <CustModal
                open={showRaiseTicketModal}
                setOpen={setShowRaiseTicketModal}
                bodyData={
                    <RaiseSupportTicket
                        onClose={() => setShowRaiseTicketModal(false)}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        setToastType={setToastType}
                    />
                }
            />
        </React.Fragment>
    );
}

export default SupportTicketListing;

